import { ChangeEvent, Fragment } from 'react'
import { Form, Row, Col, OverlayTrigger } from 'react-bootstrap'
import { FormikErrors, FormikTouched } from 'formik'
import {
  IArbitrationForm,
  IDropdownItem,
  IDocumentSubmit,
  IArbitrationDocumentInfo,
  IGetArbitrationMetadataResponse,
  IssueTypes
} from 'types/arbitrationTypes'
import { FormFieldInput } from '../FormFieldInput'
import { DragDropFileUploader } from 'components/FileUploader/DragDropFileUploader'
import { StyledTooltip } from '../ArbitrationForm.styled'
import { StyledArbitrationRequestSection } from './ArbitrationRequestSection.styled'
import { fileToBase64 } from 'utils/fileUtils'
import { useGlobalStore } from 'store/useGlobalStore'

const MAX_INPUT_LENGTH = 4000
const FILE_REQUIRED_TEXT = 'Documents required: Bill of Lading, Estimate, Photos, Transportation Invoice.'
const TITLE_ISSUE_TEXT = 'If title issue, please include scan of front/back of title and any supporting documents.'

interface IProps {
  values: IArbitrationForm
  errors: FormikErrors<IArbitrationForm>
  touched: FormikTouched<IArbitrationForm>
  handleChange: (e: ChangeEvent) => void
  setFieldValue: (field: string, value: unknown) => void
  metadata: IGetArbitrationMetadataResponse
  isUpdateAttachment: boolean
  hasAttemptedSubmit: boolean
  mileageWarning: string
  onMileageChange: (value: string) => void
  existingDocuments: IArbitrationDocumentInfo[]
}

export const ArbitrationRequestSection = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  metadata,
  isUpdateAttachment,
  hasAttemptedSubmit,
  mileageWarning,
  onMileageChange,
  existingDocuments
}: IProps) => {
  const getLocalText = useGlobalStore((state) => state.getLocalText)

  const maxFiles = metadata.MaxDocumentCount ?? 0
  const attachmentsDisabled = metadata.AttachmentsDisabled || maxFiles === 0
  const remainingSlots = maxFiles - existingDocuments.length

  const effectivePhysicalMin = metadata.PhysicalDamageMinDocuments ?? 0
  const effectiveNonPhysicalMin = metadata.NonPhysicalDamageMinDocuments ?? 0
  const currentIssueType = Number(values.issueTypeID)
  const minFiles =
    !isUpdateAttachment && currentIssueType === IssueTypes.PHYSICAL_DAMAGE
      ? effectivePhysicalMin
      : !isUpdateAttachment && currentIssueType === IssueTypes.NON_PHYSICAL_DAMAGE
      ? effectiveNonPhysicalMin
      : 0

  const handleFilesChange = async (files: File[]) => {
    const docs: IDocumentSubmit[] = await Promise.all(
      files.map(async (file) => ({
        FileName: file.name,
        FileContent: await fileToBase64(file)
      }))
    )
    setFieldValue('documents', docs)
  }

  const handleMileageInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleChange(e)
    onMileageChange(e.target.value)
  }

  return (
    <StyledArbitrationRequestSection>
      <div className="px-3 py-2">
        <h6 className="font-weight-bold">{isUpdateAttachment ? 'Add Attachments' : 'Arbitration Request'}</h6>
      </div>

      <div className="arbitration-container">
        <div className="arbitration-field">
          <label className={`${!isUpdateAttachment ? 'required-field' : ''}`}>Issue Type:</label>
          <div className="input-context">
            <Form.Control
              as="select"
              name="issueTypeID"
              value={values.issueTypeID}
              onChange={handleChange}
              disabled={isUpdateAttachment}
            >
              <option value={0}>-- Select --</option>
              {metadata.IssueTypes?.map((item: IDropdownItem) => (
                <option key={item.ID} value={item.ID}>
                  {item.Description}
                </option>
              ))}
            </Form.Control>
            {hasAttemptedSubmit && !isUpdateAttachment && Number(values.issueTypeID) === 0 && (
              <div className="error-label">Issue type is required</div>
            )}
            {!isUpdateAttachment && Number(values.issueTypeID) === IssueTypes.PHYSICAL_DAMAGE && (
              <div className="info-label">{getLocalText('ArbitrationFileRequiredText', FILE_REQUIRED_TEXT)}</div>
            )}
            {!isUpdateAttachment && Number(values.issueTypeID) === IssueTypes.NON_PHYSICAL_DAMAGE && (
              <div className="info-label">{getLocalText('ArbitrationTitleIssueText', TITLE_ISSUE_TEXT)}</div>
            )}
          </div>
        </div>
        <div className="arbitration-field">
          <label className={`${!isUpdateAttachment ? 'required-field' : ''}`}>Resolution Requested:</label>
          <div className="input-context">
            <Form.Control
              as="select"
              name="resolutionTypeID"
              value={values.resolutionTypeID}
              onChange={handleChange}
              disabled={isUpdateAttachment}
            >
              <option value={0}>-- Select --</option>
              {metadata.ResolutionTypes?.map((item: IDropdownItem) => (
                <option key={item.ID} value={item.ID}>
                  {item.Description}
                </option>
              ))}
            </Form.Control>
            {hasAttemptedSubmit && !isUpdateAttachment && Number(values.resolutionTypeID) === 0 && (
              <div className="error-label">Resolution requested is required</div>
            )}
          </div>
        </div>
      </div>

      <div className="arbitration-container">
        <div className="mileage-field">
          <FormFieldInput
            label="Current Mileage:"
            name="currentMileage"
            isInvalid={!!errors.currentMileage && (!!touched.currentMileage || hasAttemptedSubmit)}
            handleChange={handleMileageInputChange}
            defaultValue={values.currentMileage}
            error={errors.currentMileage}
            readOnly={isUpdateAttachment}
            isRequired={!isUpdateAttachment}
          />
          {!isUpdateAttachment && mileageWarning && <span className="warning-label">{mileageWarning}</span>}
        </div>
      </div>

      <div className="arbitration-textarea">
        <label className={`${!isUpdateAttachment ? 'required-field' : ''}`}>Reason:</label>
        <div className="arbitration-textarea-form">
          <Form.Control
            as="textarea"
            name="reasonForArbitration"
            rows={4}
            maxLength={MAX_INPUT_LENGTH}
            onChange={handleChange}
            defaultValue={values.reasonForArbitration}
            isInvalid={!!errors.reasonForArbitration && (!!touched.reasonForArbitration || hasAttemptedSubmit)}
            disabled={isUpdateAttachment}
          />
          <div className="char-counter">
            {(values.reasonForArbitration ?? '').length} / {MAX_INPUT_LENGTH}
          </div>
          {!!errors.reasonForArbitration && (!!touched.reasonForArbitration || hasAttemptedSubmit) && (
            <div className="error-label">{errors.reasonForArbitration}</div>
          )}
        </div>
      </div>

      {isUpdateAttachment && existingDocuments.length > 0 && (
        <div className="updated-attachment-field">
          <label>Uploaded Attachments:</label>
          <Row>
            {existingDocuments.map((doc, index) => (
              <Col xs={4} key={doc.FileName ?? index}>
                <div className="updated-attachment-file-name">
                  <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 250, hide: 400 }}
                    overlay={<StyledTooltip id={`doc-tooltip-${index}`}>{doc.FileName}</StyledTooltip>}
                  >
                    <span className="text-truncate-filename">{doc.FileName}</span>
                  </OverlayTrigger>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <div className="arbitration-attachment">
        <label
          className={`arbitration-attachment-label ${
            !attachmentsDisabled && (minFiles > 0 || isUpdateAttachment) ? 'required-field' : ''
          }`}
        >
          Attachments:
        </label>
        <div className="file-notes-label">
          {Number(values.issueTypeID) === IssueTypes.PHYSICAL_DAMAGE && (
            <>
              {getLocalText('ArbitrationFileRequiredText', FILE_REQUIRED_TEXT)} <br />
            </>
          )}
          {!attachmentsDisabled && (
            <>
              Upload {isUpdateAttachment ? remainingSlots : maxFiles} attachments as maximum, each file must not exceed{' '}
              {metadata.FileSizeLimit}MB <br />
              File types format allowed:{' '}
              {metadata.AttachmentFormats?.map((item, index) => (
                <Fragment key={index}>
                  {item}
                  {index < metadata.AttachmentFormats.length - 1 ? ', ' : ''}
                </Fragment>
              ))}
              <br />
              <br />
            </>
          )}
        </div>
      </div>

      <DragDropFileUploader
        onChange={handleFilesChange}
        acceptedFormats={metadata.AttachmentFormats}
        maxFileSizeMB={metadata.FileSizeLimit}
        maxFiles={isUpdateAttachment ? remainingSlots : maxFiles}
        disabled={attachmentsDisabled || (isUpdateAttachment && remainingSlots <= 0)}
        minFiles={minFiles}
        required={isUpdateAttachment}
        isSubmitted={hasAttemptedSubmit}
      />
    </StyledArbitrationRequestSection>
  )
}
